import { getUiLocale } from '../../../utils/uiTranslation';
import { uiText, useUiLanguage } from '../../../utils/uiTranslation';
import React from 'react';

const toDate = (value) => new Date(value);
const dayDiff = (start, end) => Math.max(0, Math.round((toDate(end) - toDate(start)) / (1000 * 60 * 60 * 24)));
const formatDate = (value) =>
  toDate(value).toLocaleDateString(getUiLocale(), { month: 'short', day: 'numeric' });

const ForecastTimelineVisual = ({ model }) => {
  useUiLanguage();
  const dataset = model.dataset;
  const rows = dataset?.rows || [];

  if (!rows.length) return null;

  if ((model.size || 'medium') === 'compact') {
    return (
      <div className="analytics-forecast-list">
        {rows.map((row) => {
          const hasTargetDate = row.meta?.hasTargetDate ?? (row.meta?.target && row.meta.target !== 'Not set');
          return (
          <div key={row.id} className="analytics-forecast-list-row">
            <div>
              <strong>{uiText(row.label)}</strong>
              <span>{row.meta?.department}</span>
            </div>
            <div className="analytics-forecast-list-values">
              <strong>{formatDate(row.predictedDate)}</strong>
              <span>{hasTargetDate ? row.meta?.target : uiText("No target set")}</span>
            </div>
          </div>
        );
        })}
      </div>
    );
  }

  const starts = rows.map((row) => toDate(row.todayDate || row.targetDate));
  const ends = rows.flatMap((row) => [toDate(row.targetDate), toDate(row.predictedDate), toDate(row.confidenceEndDate)]);
  const minDate = new Date(Math.min(...starts));
  const maxDate = new Date(Math.max(...ends));
  const totalDays = Math.max(1, dayDiff(minDate, maxDate));

  const percentForDate = (value) => (dayDiff(minDate, value) / totalDays) * 100;
  const leftForDate = (value) => `${percentForDate(value)}%`;
  const widthForRange = (start, end) => `${Math.max(1.5, (dayDiff(start, end) / totalDays) * 100)}%`;
  const edgeClassForDate = (value) => {
    const percent = percentForDate(value);
    if (percent < 10) return 'edge-start';
    if (percent > 90) return 'edge-end';
    return '';
  };

  return (
    <div className="analytics-forecast">
      <div className="analytics-forecast-key" aria-label={uiText("Vacancy forecast legend")}>
        <span className="analytics-forecast-key-item">
          <span className="analytics-forecast-key-swatch target" />{uiText("Target date")}</span>
        <span className="analytics-forecast-key-item">
          <span className="analytics-forecast-key-swatch confidence" />{uiText("Confidence range")}</span>
        <span className="analytics-forecast-key-item">
          <span className="analytics-forecast-key-swatch success" />{uiText("Forecast before target")}</span>
        <span className="analytics-forecast-key-item">
          <span className="analytics-forecast-key-swatch warning" />{uiText("Forecast close to target")}</span>
        <span className="analytics-forecast-key-item">
          <span className="analytics-forecast-key-swatch danger" />{uiText("Forecast after target")}</span>
      </div>

      <div className="analytics-forecast-rows">
        {rows.map((row) => {
          const hasTargetDate = row.meta?.hasTargetDate ?? (row.meta?.target && row.meta.target !== 'Not set');
          const forecastEdgeClass = edgeClassForDate(row.predictedDate);
          const targetEdgeClass = hasTargetDate ? edgeClassForDate(row.targetDate) : '';
          return (
          <div key={row.id} className="analytics-forecast-row">
            <div className="analytics-forecast-row-head">
              <div className="analytics-forecast-meta">
                <strong>{uiText(row.label)}</strong>
                <span>{row.meta?.department}</span>
              </div>
              <div className="analytics-forecast-row-values">
                <span><strong>{uiText("Forecast")}</strong>{row.meta?.forecast}</span>
                <span><strong>{uiText("Target")}</strong>{hasTargetDate ? row.meta?.target : uiText("Not set")}</span>
                <span><strong>{uiText("Confidence")}</strong>{row.meta?.confidence}</span>
              </div>
            </div>

            <div className="analytics-forecast-track">
              <span className="analytics-forecast-row-axis start">{formatDate(minDate)}</span>
              <span className="analytics-forecast-row-axis end">{formatDate(maxDate)}</span>
              {hasTargetDate ? <span className="analytics-forecast-target-line" style={{ left: leftForDate(row.targetDate) }} /> : null}
              <span
                className="analytics-forecast-confidence"
                style={{
                  left: leftForDate(row.confidenceStartDate),
                  width: widthForRange(row.confidenceStartDate, row.confidenceEndDate)
                }}
              />
              <span
                className={`analytics-forecast-bar ${row.status || 'accent'}`}
                style={{
                  left: leftForDate(row.todayDate || minDate),
                  width: widthForRange(row.todayDate || minDate, row.predictedDate)
                }}
              />
              <span
                className={`analytics-forecast-forecast-label ${row.status || 'accent'} ${forecastEdgeClass}`}
                style={{ left: leftForDate(row.predictedDate) }}
              >
                <strong>{uiText("Forecast")}</strong>
                {row.meta?.forecast}
              </span>
              {hasTargetDate ? (
                <span
                  className={`analytics-forecast-target-label ${targetEdgeClass}`}
                  style={{ left: leftForDate(row.targetDate) }}
                >
                  <strong>{uiText("Target")}</strong>
                  {row.meta?.target}
                </span>
              ) : null}
            </div>
          </div>
        );
        })}
      </div>
    </div>
  );
};

export default ForecastTimelineVisual;
